import { useState } from "react";
import EnergyQuizModal from "./EnergyQuizModal";

export default function QuizSection() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=mystical%20dark%20background%20with%20soft%20golden%20light%20bokeh%20particles%20spiritual%20energy%20abstract%20texture%20warm%20amber%20tones%20elegant%20luxury%20atmosphere%20deep%20dark%20brown&width=1600&height=700&seq=quiz_section_bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1208]/90 via-[#1a1208]/80 to-[#1a1208]/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            {/* Left: Text */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#8b6f47]/30 border border-[#8b6f47]/50 mb-6">
                <i className="ri-sparkling-2-line text-[#c9a96e] text-sm"></i>
                <span className="text-[#c9a96e] text-xs tracking-widest uppercase" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                  能量測驗
                </span>
              </div>

              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                找到屬於你的
                <br />
                <span className="text-[#c9a96e]">能量飾品</span>
              </h2>

              <p
                className="text-white/70 text-base md:text-lg leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                每個人的能量頻率都不同，透過 5 個問題，
                我們將為你精準推薦最契合的能量飾品，
                讓每一件飾品都成為你獨特的守護。
              </p>

              {/* Stats */}
              <div className="flex items-center justify-center lg:justify-start gap-8 mb-10">
                {[
                  { num: "5", label: "個問題" },
                  { num: "1", label: "分鐘完成" },
                  { num: "4", label: "種能量類型" },
                ].map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="text-2xl font-bold text-[#c9a96e]" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                      {s.num}
                    </p>
                    <p className="text-white/50 text-xs mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#8b6f47] hover:bg-[#7a5f3a] text-white rounded-full text-base font-semibold cursor-pointer transition-all duration-300 whitespace-nowrap group"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                <i className="ri-sparkling-2-line text-lg"></i>
                立即開始測驗
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
              </button>
            </div>

            {/* Right: Visual cards */}
            <div className="flex-shrink-0 w-full lg:w-auto">
              <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
                {[
                  {
                    icon: "ri-ancient-gate-line",
                    label: "西藏天珠",
                    sub: "守護 · 財富 · 事業",
                    color: "#8b6f47",
                    bg: "rgba(139,111,71,0.15)",
                    border: "rgba(139,111,71,0.3)",
                    img: "https://readdy.ai/api/search-image?query=nine%20eye%20Tibetan%20dzi%20bead%20bracelet%20on%20dark%20stone%20surface%20warm%20golden%20light%20mystical%20ancient%20energy%20jewelry%20photography%20close%20up&width=300&height=200&seq=quiz_card_dzi&orientation=landscape",
                  },
                  {
                    icon: "ri-sparkling-2-line",
                    label: "天然水晶",
                    sub: "療癒 · 平靜 · 直覺",
                    color: "#9b8bc0",
                    bg: "rgba(122,106,154,0.15)",
                    border: "rgba(122,106,154,0.3)",
                    img: "https://readdy.ai/api/search-image?query=amethyst%20purple%20crystal%20bracelet%20on%20dark%20background%20soft%20purple%20light%20healing%20energy%20jewelry%20photography%20close%20up%20elegant&width=300&height=200&seq=quiz_card_crystal&orientation=landscape",
                  },
                  {
                    icon: "ri-leaf-line",
                    label: "翡翠玉石",
                    sub: "吉祥 · 和諧 · 典雅",
                    color: "#5a9a62",
                    bg: "rgba(74,122,82,0.15)",
                    border: "rgba(74,122,82,0.3)",
                    img: "https://readdy.ai/api/search-image?query=jade%20jadeite%20green%20pendant%20on%20dark%20background%20soft%20natural%20light%20oriental%20elegant%20jewelry%20photography%20close%20up%20cultural%20heritage&width=300&height=200&seq=quiz_card_jade&orientation=landscape",
                  },
                  {
                    icon: "ri-shield-star-line",
                    label: "唐卡佛牌",
                    sub: "信仰 · 智慧 · 加持",
                    color: "#c9a96e",
                    bg: "rgba(201,169,110,0.15)",
                    border: "rgba(201,169,110,0.3)",
                    img: "https://readdy.ai/api/search-image?query=Tibetan%20Buddhist%20amulet%20pendant%20gold%20sacred%20charm%20on%20dark%20background%20warm%20golden%20light%20spiritual%20protection%20collectible%20jewelry%20photography%20close%20up&width=300&height=200&seq=quiz_card_thangka&orientation=landscape",
                  },
                ].map((cat) => (
                  <div
                    key={cat.label}
                    className="rounded-2xl overflow-hidden cursor-pointer group"
                    style={{ background: cat.bg, border: `1px solid ${cat.border}` }}
                    onClick={() => setOpen(true)}
                  >
                    <div className="h-28 overflow-hidden">
                      <img
                        src={cat.img}
                        alt={cat.label}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full" style={{ background: cat.color + "30" }}>
                          <i className={`${cat.icon} text-xs`} style={{ color: cat.color }}></i>
                        </div>
                        <span
                          className="text-xs font-semibold text-white"
                          style={{ fontFamily: "'Noto Serif TC', serif" }}
                        >
                          {cat.label}
                        </span>
                      </div>
                      <p className="text-white/50 text-[10px]">{cat.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-center text-white/40 text-xs mt-4">點擊任一類型開始測驗</p>
            </div>
          </div>
        </div>
      </section>

      <EnergyQuizModal isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
}
